const exampleGmMoves = [
  'Show how the world reacts',
  'Ask a question and build on the answer',
  'Make an NPC act in accordance with their motive',
  'Drive a PC to take action by dangling their goals in front of them',
  'Signal an imminent off-screen threat',
  'Reveal an unwelcome truth or unexpected danger',
  'Force the group to split up',
  'Make a PC mark Stress',
  'Make a move the characters don\'t see',
  'Show the collateral damage',
  'Clear an adversary\'s condition',
  'Shift the environment',
  'Spotlight an adversary',
  'Capture someone or something important',
  'Use a PC\'s backstory against them',
  'Take away an opportunity permanently.',
];

const fearSpendEntries = [
  { level: 'Incidental', desc: "A catch-up between PCs after an emotionally charged scene; gathering information; resupplying at a local market; resting during downtime.", range: '0–1 Fear' },
  { level: 'Minor', desc: 'A travel sequence; a minor skirmish that introduces new foes or signals future trouble.', range: '1–3 Fear' },
  { level: 'Standard', desc: 'A substantial battle with a notable objective; perilous travel that tests might and wit; a tense social encounter seeking crucial information or aid.', range: '2–4 Fear' },
  { level: 'Major', desc: "A large battle with a Solo or Leader adversary; a character-defining scene with a significant change to a character's personal story (such as revelation, growth, and betrayal).", range: '4–8 Fear' },
  { level: 'Climactic', desc: "A major confrontation with the villain of a story arc; an epic set piece battle; a judicial duel to determine an important NPC's fate.", range: '6–12 Fear' },
];

export default function FearMovesContent() {
  return (
    <>
      <h3 id="making-moves">MAKING MOVES</h3>
      <p>As the GM, you have <strong>GM moves</strong> that change the story in response to the players' actions. GM moves aren't bound by specific spells or effects — when you make a GM move, you can describe the action in whatever way the fiction demands.</p>
      <p>GM moves happen during <strong>GM turns</strong>. A GM turn begins when the spotlight passes to them and ends when the spotlight passes back to the players.</p>

      <h3 id="when-to-make-a-move">WHEN TO MAKE A MOVE</h3>
      <p>The GM can make a GM move whenever you want, but the frequency and severity depends on the type of story you're telling, the actions your players take, and the tone of the session you're running.</p>
      <p>Make a GM move when the players:</p>
      <ul className="two-col-list">
        <li>Roll with Fear</li>
        <li>Fail an action roll</li>
        <li>Do something that has unavoidable consequences</li>
        <li>Give you a "golden opportunity" (an opening that demands an immediate response)</li>
        <li>Look to you for what happens next</li>
      </ul>

      <h3 id="choosing-gm-moves">CHOOSING GM MOVES</h3>
      <p>The result of a player's action roll determines your response:</p>
      <div className="feature-grid">
        <div className="feature-block feature-block--solo">
          <div className="feature-name">ON A CRITICAL SUCCESS</div>
          <div className="feature-text">Let the player describe their success, then give them an additional opportunity or advantage.</div>
        </div>
        <div className="feature-block">
          <div className="feature-name">ON A SUCCESS WITH HOPE</div>
          <div className="feature-text">Let the player describe their success, then show how the world reacts to it.</div>
        </div>
        <div className="feature-block">
          <div className="feature-name">ON A SUCCESS WITH FEAR</div>
          <div className="feature-text">
            Work with the player to describe their success, then take a Fear and make a GM move to introduce a minor consequence, complication, or cost.
            <details className="feature-details">
              <summary>Examples</summary>
              <ul>
                <li>An adversary attacks</li>
                <li>The PC marks a Stress</li>
                <li>You introduce a new threat</li>
                <li>You raise the stakes of the conflict</li>
              </ul>
            </details>
          </div>
        </div>
        <div className="feature-block feature-block--failure">
          <div className="feature-name">ON A FAILURE WITH HOPE</div>
          <div className="feature-text">
            Describe how the PC fails to get what they want, then make a GM move to introduce a minor consequence, complication, or cost.
            <details className="feature-details">
              <summary>Examples</summary>
              <ul>
                <li>An adversary attacks</li>
                <li>The PC marks a Stress</li>
                <li>You introduce a new threat</li>
                <li>You raise the stakes of the conflict</li>
              </ul>
            </details>
          </div>
        </div>
        <div className="feature-block feature-block--failure">
          <div className="feature-name">ON A FAILURE WITH FEAR</div>
          <div className="feature-text">
            Describe how things go wrong, then make a GM move to introduce a major consequence, complication, or cost.
            <details className="feature-details">
              <summary>Examples</summary>
              <ul>
                <li>You put them in immediate danger</li>
                <li>They become the focus of multiple adversaries</li>
                <li>They are separated from their party</li>
                <li>They lose an important opportunity for good.</li>
              </ul>
            </details>
          </div>
        </div>
      </div>

      <h3 id="quick-reference">QUICK REFERENCE: RESOLVING ACTION ROLLS</h3>
      <p className="rule-note">If you're unsure how to resolve a roll, think about these quick phrases:</p>
      <ul>
        <li><strong>Success with Hope:</strong> Yes, and... (You get what you want and gain a Hope.)</li>
        <li><strong>Success with Fear:</strong> Yes, but... (You get what you want, but there's a consequence, and the GM gains a Fear.)</li>
        <li><strong>Failure with Hope:</strong> No, but... (Things don't go as planned, but you gain a Hope.)</li>
        <li><strong>Failure with Fear:</strong> No, and... (Things don't go as planned and it gets worse. The GM gains a Fear.)</li>
      </ul>
      <p>If the move you should make isn't obvious from the fiction, draw inspiration from the "Example GM Moves" list:</p>
      <ul className="two-col-list">
        <li>Introduce a new obstacle or enemy</li>
        <li>Ask the player what happens</li>
        <li>Have the PC mark a Stress</li>
        <li>Tell the players "everything is fine... for now."</li>
      </ul>

      <h3 id="soft-hard-moves">SOFT AND HARD MOVES</h3>
      <p><strong>Soft moves</strong> go easier on the players — they give the party new information about the scene and offer them an opportunity to react to it. <strong>Hard moves</strong> are harsher, more impactful, or more direct — the PCs don't get an opening to interrupt, alter, or anticipate the outcome.</p>
      <p>Use softer moves on rolls with Hope and harder moves on rolls with Fear.</p>

      <h3 id="example-gm-moves">EXAMPLE GM MOVES</h3>
      <ul className="two-col-list">
        {exampleGmMoves.map((m) => <li key={m}>{m}</li>)}
      </ul>

      <h3 id="using-fear">USING FEAR</h3>
      <p>You start a campaign with 1 Fear per PC in the party.</p>
      <p>You gain Fear whenever a PC rolls with Fear, the PCs take a rest (see: Downtime), or when an ability or effect tells you to.</p>
      <p>You can never have more than 12 Fear at one time.</p>
      <p>Fear carries over between sessions.</p>
      <p>Spend a Fear to:</p>
      <ul className="two-col-list">
        <li>Interrupt the players to steal the spotlight and make a move</li>
        <li>Make an additional GM move</li>
        <li>Use an adversary's Fear Feature</li>
        <li>Use an environment's Fear Feature</li>
        <li>Add an adversary's Experience to a roll</li>
      </ul>

      <h3 id="fear-spending">FEAR SPENDING BY SCENE TYPE</h3>
      <table className="equipment-table" style={{ marginBottom: '16px' }}>
        <thead>
          <tr>
            <th className="fear-scene-col">Scene Type</th>
            <th>Description</th>
            <th className="fear-range-col">Fear Range</th>
          </tr>
        </thead>
        <tbody>
          {fearSpendEntries.map((e) => (
            <tr key={e.level}>
              <td className="fear-scene-col"><strong>{e.level}</strong></td>
              <td style={{ fontSize: '13px' }}>{e.desc}</td>
              <td className="fear-range-col">{e.range}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <p>If you find yourself with a large amount of Fear, consider:</p>
      <ul>
        <li><strong>Spending Fast:</strong> Spend Fear before the players have a chance to react</li>
        <li><strong>Spending Often:</strong> Spend Fear every time the spotlight swings to you</li>
        <li><strong>Spending Big:</strong> Spend Fear to make multiple moves in a row</li>
      </ul>
      <p>Spending Fear to make a move communicates the increased impact of your action. Fear moves often include one or more of these elements:</p>
      <ul>
        <li>Introducing new adversaries to a scene when their appearance hasn't been foreshadowed or lacks context.</li>
        <li>An adversary activating a powerful spell or transformation to deal massive damage or boost their capabilities.</li>
        <li>An environment exerting a strong negative effect on the party.</li>
      </ul>

      <h3 id="guidance-action-rolls">GUIDANCE ON ACTION ROLLS</h3>
      <p>After a player describes a move they want to make during the game, you might decide an action roll is necessary to determine how the scene progresses. Use this guide to determine what to present to the player, choosing whichever option best fits the situation:</p>
      <ul>
        <li>Determine whether the roll is necessary, considering the PC's Experiences or backstory, the pressure they're acting under, and the possible outcomes.</li>
        <li>Establish the stakes of an action roll before the player makes it.</li>
        <li>Communicate any unavoidable consequences.</li>
        <li>If desired, you can offer the player the opportunity to forgo an action roll in exchange for agreeing to an interesting outcome, cost, or complication.</li>
      </ul>
    </>
  );
}
